import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api, { kaireraAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle, Brain,
  Sparkles, ChevronRight, BarChart3, Play, RotateCcw,
  Lock, TrendingUp, MapPin, DollarSign, GraduationCap,
  AlertTriangle, Target, User, Briefcase, Clock, Compass,
  Lightbulb
} from 'lucide-react';

const PHASE_META = {
  0: { label: 'Goal Setting', color: '#F5841F', questions: 'Start' },
  1: { label: 'Profile Foundation', color: '#0D9488', questions: '1-5' },
  2: { label: 'Aspirations & Context', color: '#1B3461', questions: '6-10' },
  3: { label: 'Deep Dive', color: '#F5841F', questions: '11-15' },
};

const QUESTION_ICONS = {
  Q0: Compass, Q1: Briefcase, Q2: User, Q3: Clock, Q4: MapPin, Q5: MapPin,
  Q6: Target, Q7: TrendingUp, Q8: DollarSign, Q9: GraduationCap,
  Q10: AlertTriangle, Q11: Brain, Q12: User, Q13: TrendingUp,
  Q14: Clock, Q15: Sparkles,
};

// ---- Input Components ----

function SelectInput({ question, value, onChange }) {
  const [otherText, setOtherText] = useState('');
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {question.options.map(opt => (
          <button key={opt} onClick={() => onChange(opt)} data-testid={`option-${opt}`}
            className="text-left px-4 py-3 rounded-lg text-sm font-medium transition-all border"
            style={{
              background: value === opt ? 'rgba(13,148,136,0.1)' : 'var(--t-bg-card)',
              borderColor: value === opt ? '#0D9488' : 'var(--t-border)',
              color: value === opt ? '#0D9488' : 'var(--t-text)',
            }}>
            {opt}
          </button>
        ))}
      </div>
      {question.allow_other && (
        <div className="mt-3">
          <Input placeholder="Or type your industry..." value={otherText}
            onChange={e => { setOtherText(e.target.value); onChange(e.target.value); }}
            className="h-11" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
      )}
    </div>
  );
}

function TextInput({ question, value, onChange }) {
  return (
    <Input data-testid={`input-${question.id}`} value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={question.placeholder || ''}
      className="h-12 text-base" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
  );
}

function TextAreaInput({ question, value, onChange }) {
  return (
    <textarea data-testid={`input-${question.id}`} value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={question.placeholder || ''}
      rows={4}
      className="w-full rounded-lg border px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
      style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
  );
}

function DualNumberInput({ question, value, onChange }) {
  const val = value || {};
  return (
    <div className="grid grid-cols-2 gap-4">
      {question.fields.map(f => (
        <div key={f.key}>
          <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>{f.label}</Label>
          <Input type="number" min={f.min} max={f.max} value={val[f.key] || ''}
            onChange={e => onChange({ ...val, [f.key]: parseInt(e.target.value) || 0 })}
            className="h-11" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
      ))}
    </div>
  );
}

function GeographyInput({ question, value, onChange }) {
  const val = value || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Country</Label>
          <Input value={val.country || ''} onChange={e => onChange({ ...val, country: e.target.value })}
            placeholder="e.g., UAE" className="h-11"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>City</Label>
          <Input value={val.city || ''} onChange={e => onChange({ ...val, city: e.target.value })}
            placeholder="e.g., Dubai" className="h-11"
            style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <div className="relative">
          <input type="checkbox" checked={val.open_to_relocation || false}
            onChange={e => onChange({ ...val, open_to_relocation: e.target.checked })}
            className="sr-only" />
          <div className="w-10 h-5 rounded-full transition-colors"
            style={{ background: val.open_to_relocation ? '#0D9488' : 'var(--t-border)' }}>
            <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform"
              style={{ transform: val.open_to_relocation ? 'translateX(20px)' : 'translateX(0)' }} />
          </div>
        </div>
        <span className="text-sm" style={{ color: 'var(--t-text-secondary)' }}>Open to relocation</span>
      </label>
    </div>
  );
}


function PathwaySelectInput({ question, value, onChange }) {
  return (
    <div className="space-y-3" data-testid="pathway-select">
      {question.options.map((opt, i) => (
        <motion.button key={opt.key}
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.08 }}
          onClick={() => onChange(opt.key)}
          className="w-full text-left p-4 rounded-xl border-2 transition-all"
          style={{
            background: value === opt.key ? 'rgba(13,148,136,0.06)' : 'var(--t-bg-card)',
            borderColor: value === opt.key ? '#0D9488' : 'var(--t-border)',
          }}
          data-testid={`pathway-${opt.key}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: value === opt.key ? '#0D9488' : 'var(--t-bg-section)' }}>
              <Compass className="w-5 h-5" style={{ color: value === opt.key ? '#fff' : 'var(--t-text-muted)' }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: value === opt.key ? '#0D9488' : 'var(--t-text-heading)' }}>
                {opt.label}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{opt.desc}</p>
            </div>
            {value === opt.key && <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#0D9488' }} />}
          </div>
        </motion.button>
      ))}
    </div>
  );
}

function SalaryInput({ question, value, onChange }) {
  const val = value || { currency: 'USD' };
  const currencies = question.fields.find(f => f.key === 'currency')?.options || ['USD'];
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Currency</Label>
        <div className="flex gap-2 flex-wrap">
          {currencies.map(c => (
            <button key={c} onClick={() => onChange({ ...val, currency: c })}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
              style={{
                background: val.currency === c ? 'rgba(13,148,136,0.1)' : 'transparent',
                borderColor: val.currency === c ? '#0D9488' : 'var(--t-border)',
                color: val.currency === c ? '#0D9488' : 'var(--t-text-muted)',
              }}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Current Total Comp</Label>
          <Input type="number" value={val.current_salary || ''} placeholder="e.g., 120000"
            onChange={e => onChange({ ...val, current_salary: parseInt(e.target.value) || 0 })}
            className="h-11" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Target Total Comp</Label>
          <Input type="number" value={val.target_salary || ''} placeholder="e.g., 180000"
            onChange={e => onChange({ ...val, target_salary: parseInt(e.target.value) || 0 })}
            className="h-11" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
        </div>
      </div>
    </div>
  );
}

function EducationInput({ question, value, onChange }) {
  const val = value || {};
  const edField = question.fields.find(f => f.key === 'education_level');
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Highest Education</Label>
        <div className="grid grid-cols-2 gap-2">
          {edField?.options.map(opt => (
            <button key={opt} onClick={() => onChange({ ...val, education_level: opt })}
              className="text-left px-3 py-2 rounded-lg text-xs font-medium border transition-colors"
              style={{
                background: val.education_level === opt ? 'rgba(13,148,136,0.1)' : 'var(--t-bg-card)',
                borderColor: val.education_level === opt ? '#0D9488' : 'var(--t-border)',
                color: val.education_level === opt ? '#0D9488' : 'var(--t-text)',
              }}>
              {opt}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Professional Certifications</Label>
        <Input value={val.certifications || ''} placeholder="e.g., PMP, AWS SA, ISTQB"
          onChange={e => onChange({ ...val, certifications: e.target.value })}
          className="h-11" style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
      </div>
    </div>
  );
}

function SkillsInput({ question, value, onChange }) {
  const val = value || { strengths: [], improvements: [] };
  const [sInput, setSInput] = useState('');
  const [iInput, setIInput] = useState('');

  const addStrength = () => {
    if (sInput.trim() && (val.strengths?.length || 0) < 3) {
      const updated = { ...val, strengths: [...(val.strengths || []), sInput.trim()] };
      onChange(updated);
      setSInput('');
    }
  };
  const addImprovement = () => {
    if (iInput.trim() && (val.improvements?.length || 0) < 2) {
      const updated = { ...val, improvements: [...(val.improvements || []), iInput.trim()] };
      onChange(updated);
      setIInput('');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-semibold mb-2 block" style={{ color: 'var(--t-text-muted)' }}>
          Top 3 Strengths ({val.strengths?.length || 0}/3)
        </Label>
        <div className="flex gap-2 flex-wrap mb-2">
          {val.strengths?.map((s, i) => (
            <span key={i} className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
              style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488' }}>
              {s}
              <button onClick={() => onChange({ ...val, strengths: val.strengths.filter((_, j) => j !== i) })}
                className="ml-1 opacity-60 hover:opacity-100">&times;</button>
            </span>
          ))}
        </div>
        {(val.strengths?.length || 0) < 3 && (
          <div className="flex gap-2">
            <Input value={sInput} onChange={e => setSInput(e.target.value)} placeholder="e.g., Strategic planning"
              onKeyDown={e => e.key === 'Enter' && addStrength()} className="h-10 flex-1"
              style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
            <Button onClick={addStrength} style={{ background: '#0D9488', color: '#fff' }} className="h-10 px-3">Add</Button>
          </div>
        )}
      </div>
      <div>
        <Label className="text-xs font-semibold mb-2 block" style={{ color: 'var(--t-text-muted)' }}>
          2 Areas to Improve ({val.improvements?.length || 0}/2)
        </Label>
        <div className="flex gap-2 flex-wrap mb-2">
          {val.improvements?.map((s, i) => (
            <span key={i} className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
              style={{ background: 'rgba(245,132,31,0.1)', color: '#F5841F' }}>
              {s}
              <button onClick={() => onChange({ ...val, improvements: val.improvements.filter((_, j) => j !== i) })}
                className="ml-1 opacity-60 hover:opacity-100">&times;</button>
            </span>
          ))}
        </div>
        {(val.improvements?.length || 0) < 2 && (
          <div className="flex gap-2">
            <Input value={iInput} onChange={e => setIInput(e.target.value)} placeholder="e.g., Public speaking"
              onKeyDown={e => e.key === 'Enter' && addImprovement()} className="h-10 flex-1"
              style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
            <Button onClick={addImprovement} style={{ background: '#F5841F', color: '#fff' }} className="h-10 px-3">Add</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function NetworkInput({ question, value, onChange }) {
  const val = value || { network_rating: 5, has_mentors: false };
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-xs font-semibold mb-3 block" style={{ color: 'var(--t-text-muted)' }}>
          Network Strength: <span style={{ color: '#0D9488', fontWeight: 800 }}>{val.network_rating}/10</span>
        </Label>
        <Slider value={[val.network_rating]} min={1} max={10} step={1}
          onValueChange={v => onChange({ ...val, network_rating: v[0] })} />
        <div className="flex justify-between mt-1">
          <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>Isolated</span>
          <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>Well-connected</span>
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <div className="relative">
          <input type="checkbox" checked={val.has_mentors}
            onChange={e => onChange({ ...val, has_mentors: e.target.checked })}
            className="sr-only" />
          <div className="w-10 h-5 rounded-full transition-colors"
            style={{ background: val.has_mentors ? '#0D9488' : 'var(--t-border)' }}>
            <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform"
              style={{ transform: val.has_mentors ? 'translateX(20px)' : 'translateX(0)' }} />
          </div>
        </div>
        <span className="text-sm" style={{ color: 'var(--t-text-secondary)' }}>I have mentors/sponsors in leadership positions</span>
      </label>
    </div>
  );
}

function SliderInput({ question, value, onChange }) {
  const v = value || 5;
  return (
    <div>
      <div className="text-center mb-4">
        <span className="text-4xl font-extrabold" style={{ color: '#0D9488' }}>{v}</span>
        <span className="text-lg" style={{ color: 'var(--t-text-muted)' }}>/10</span>
      </div>
      <Slider value={[v]} min={question.min} max={question.max} step={1}
        onValueChange={val => onChange(val[0])} />
      <div className="flex justify-between mt-2">
        {question.labels && Object.entries(question.labels).map(([k, label]) => (
          <span key={k} className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function MultiSelectInput({ question, value, onChange }) {
  const selected = value || [];
  const toggle = (opt) => {
    if (opt === "No external pressure") {
      onChange(selected.includes(opt) ? [] : [opt]);
      return;
    }
    const filtered = selected.filter(s => s !== "No external pressure");
    onChange(filtered.includes(opt) ? filtered.filter(s => s !== opt) : [...filtered, opt]);
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      {question.options.map(opt => (
        <button key={opt} onClick={() => toggle(opt)}
          className="text-left px-4 py-3 rounded-lg text-sm font-medium border transition-all"
          style={{
            background: selected.includes(opt) ? 'rgba(13,148,136,0.1)' : 'var(--t-bg-card)',
            borderColor: selected.includes(opt) ? '#0D9488' : 'var(--t-border)',
            color: selected.includes(opt) ? '#0D9488' : 'var(--t-text)',
          }}>
          <div className="flex items-center gap-2">
            {selected.includes(opt)
              ? <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#0D9488' }} />
              : <div className="w-4 h-4 rounded-full border flex-shrink-0" style={{ borderColor: 'var(--t-border)' }} />}
            {opt}
          </div>
        </button>
      ))}
    </div>
  );
}

function TextWithSelectInput({ question, value, onChange }) {
  const [mode, setMode] = useState('text');
  return (
    <div className="space-y-3">
      <div className="flex gap-2 mb-2">
        <button onClick={() => setMode('text')}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
          style={{ background: mode === 'text' ? 'rgba(13,148,136,0.1)' : 'transparent', borderColor: mode === 'text' ? '#0D9488' : 'var(--t-border)', color: mode === 'text' ? '#0D9488' : 'var(--t-text-muted)' }}>
          Type company name
        </button>
        <button onClick={() => setMode('select')}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
          style={{ background: mode === 'select' ? 'rgba(13,148,136,0.1)' : 'transparent', borderColor: mode === 'select' ? '#0D9488' : 'var(--t-border)', color: mode === 'select' ? '#0D9488' : 'var(--t-text-muted)' }}>
          Select company size
        </button>
      </div>
      {mode === 'text' ? (
        <Input value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)}
          placeholder={question.placeholder} className="h-11"
          style={{ background: 'var(--t-bg-input)', borderColor: 'var(--t-border)', color: 'var(--t-text)' }} />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {question.fallback_options.map(opt => (
            <button key={opt} onClick={() => onChange(opt)}
              className="text-left px-3 py-2.5 rounded-lg text-xs font-medium border transition-colors"
              style={{
                background: value === opt ? 'rgba(13,148,136,0.1)' : 'var(--t-bg-card)',
                borderColor: value === opt ? '#0D9488' : 'var(--t-border)',
                color: value === opt ? '#0D9488' : 'var(--t-text)',
              }}>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Input renderer
function QuestionInput({ question, value, onChange }) {
  switch (question.input_type) {
    case 'pathway_select': return <PathwaySelectInput question={question} value={value} onChange={onChange} />;
    case 'select': return <SelectInput question={question} value={value} onChange={onChange} />;
    case 'text': return <TextInput question={question} value={value} onChange={onChange} />;
    case 'textarea': return <TextAreaInput question={question} value={value} onChange={onChange} />;
    case 'dual_number': return <DualNumberInput question={question} value={value} onChange={onChange} />;
    case 'geography': return <GeographyInput question={question} value={value} onChange={onChange} />;
    case 'salary': return <SalaryInput question={question} value={value} onChange={onChange} />;
    case 'education': return <EducationInput question={question} value={value} onChange={onChange} />;
    case 'skills': return <SkillsInput question={question} value={value} onChange={onChange} />;
    case 'network': return <NetworkInput question={question} value={value} onChange={onChange} />;
    case 'slider': return <SliderInput question={question} value={value} onChange={onChange} />;
    case 'multi_select': return <MultiSelectInput question={question} value={value} onChange={onChange} />;
    case 'text_with_select': return <TextWithSelectInput question={question} value={value} onChange={onChange} />;
    default: return <TextInput question={question} value={value} onChange={onChange} />;
  }
}

// ---- AI Hint Box ----
function HintBox({ hint }) {
  if (!hint) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-lg p-3 mt-3 flex items-start gap-2.5"
      style={{ background: 'rgba(13,148,136,0.04)', border: '1px solid rgba(13,148,136,0.12)' }}
      data-testid="question-hint-box">
      <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#0D9488' }} />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#0D9488' }}>
          Smart Suggestion based on your profile
        </p>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>{hint}</p>
      </div>
    </motion.div>
  );
}

// ---- MAIN PAGE ----

export default function KaireraAssessmentPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [insight, setInsight] = useState(null);
  const [showInsight, setShowInsight] = useState(false);
  const [gradeUpdates, setGradeUpdates] = useState([]);
  const [resumed, setResumed] = useState(false);
  const [hints, setHints] = useState({});
  const [showGoalIntro, setShowGoalIntro] = useState(false);
  const navigate = useNavigate();

  const initSession = useCallback(async () => {
    try {
      setLoading(true);
      const [sessionRes, hintsRes] = await Promise.allSettled([
        kaireraAPI.createSession('full'),
        api.get('/kairera/question-hints'),
      ]);

      if (sessionRes.status === 'fulfilled') {
        const data = sessionRes.value.data;
        setSession(data.session);
        setQuestions(data.questions);
        setResumed(data.resumed);
        const ansMap = {};
        (data.answers || []).forEach(a => { ansMap[a.question_id] = a.answer_value; });
        setAnswers(ansMap);
        const answeredCount = data.answered_ids?.length || 0;
        setCurrentIdx(Math.min(answeredCount, data.questions.length - 1));
        // Show goal intro only for brand new sessions (no goal_name set, no answers)
        if (answeredCount === 0 && !data.session?.goal_name) {
          setShowGoalIntro(true);
        }
      }

      if (hintsRes.status === 'fulfilled') {
        setHints(hintsRes.value.data?.hints || {});
      }
    } catch (err) {
      console.error('Session init failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { initSession(); }, [initSession]);

  const currentQ = questions[currentIdx];
  const currentPhase = currentQ?.phase || 1;
  const phaseMeta = PHASE_META[currentPhase];
  const answeredCount = Object.keys(answers).length;
  const totalQ = questions.length;
  const progressPct = totalQ > 0 ? Math.round((answeredCount / totalQ) * 100) : 0;
  const isAnswered = currentQ && answers[currentQ.id] !== undefined;
  const QIcon = currentQ ? (QUESTION_ICONS[currentQ.id] || Brain) : Brain;

  const handleSubmit = async () => {
    if (!currentQ || !answers[currentQ.id]) return;
    setSubmitting(true);
    setInsight(null);
    setShowInsight(false);

    try {
      const res = await kaireraAPI.submitAnswer(currentQ.id, answers[currentQ.id]);
      const data = res.data;

      if (data.insight) {
        setInsight(data.insight);
        setShowInsight(true);
        setGradeUpdates(data.grade_updates || []);
      }

      if (data.discovery_complete) {
        setTimeout(() => navigate('/career-intel'), 2000);
      } else if (data.next_question_index !== null) {
        setTimeout(() => {
          setShowInsight(false);
          setCurrentIdx(data.next_question_index);
        }, data.insight ? 3500 : 500);
      }
    } catch (err) {
      console.error('Submit failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Start a fresh assessment? Your current progress will be archived.')) {
      try {
        await kaireraAPI.resetSession();
        setAnswers({});
        setCurrentIdx(0);
        setSession(null);
        setInsight(null);
        setShowInsight(false);
        setGradeUpdates([]);
        setResumed(false);
        // Re-initialize with fresh session
        setLoading(true);
        const res = await kaireraAPI.createSession('full');
        const data = res.data;
        setSession(data.session);
        setQuestions(data.questions);
        setResumed(false);
        setCurrentIdx(0);
        setAnswers({});
      } catch (err) {
        console.error('Reset failed:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#0D9488' }} />
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>Initializing Career Intelligence...</p>
        </div>
      </div>
    );
  }

  // Goal setting intro screen
  if (showGoalIntro) {
    return (
      <div className="max-w-2xl mx-auto py-8" data-testid="goal-intro-screen">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
              style={{ background: 'rgba(13,148,136,0.08)' }}>
              <Target className="w-8 h-8" style={{ color: '#0D9488' }} />
            </div>
            <h1 className="text-2xl font-extrabold mb-3" style={{ color: 'var(--t-text-heading)' }}>
              Before We Begin
            </h1>
            <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--t-text-muted)' }}>
              Kairera builds a personalized career intelligence report. To do this well, we need to understand your career goal first. This drives every insight, grade, and recommendation we generate.
            </p>
          </div>

          <div className="nx-card p-6 mb-6" style={{ borderColor: 'rgba(13,148,136,0.15)' }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--t-text-heading)' }}>
              What to expect
            </h3>
            <div className="space-y-3">
              {[
                { icon: Brain, text: '16 questions about your career, skills, and aspirations', sub: 'Takes about 8 minutes' },
                { icon: Sparkles, text: 'Live insights generated as you answer each question', sub: 'Personalized to your profile' },
                { icon: BarChart3, text: 'A complete Career Intelligence report with grades and recommendations', sub: 'Requires Pro plan to view full results' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'var(--t-bg-page)' }}>
                  <item.icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#0D9488' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>{item.text}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button onClick={() => setShowGoalIntro(false)} data-testid="start-discovery-btn"
            className="h-12 px-8 font-bold rounded-lg w-full"
            style={{ background: '#0D9488', color: '#fff' }}>
            Define My Career Goal <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="kairera-assessment">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0D9488' }}>
              Career Intelligence Assessment
            </p>
            <h1 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
              Discovery Phase
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/career-intel')} className="text-xs font-medium flex items-center gap-1"
              style={{ color: '#0D9488' }} data-testid="view-dashboard-btn">
              <BarChart3 className="w-3.5 h-3.5" /> Dashboard
            </button>
            <button onClick={handleReset} className="text-xs flex items-center gap-1"
              style={{ color: 'var(--t-text-muted)' }} data-testid="reset-btn">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-4 mb-2">
          {[1, 2, 3].map(phase => {
            const phaseStart = phase === 1 ? 1 : phase === 2 ? 6 : 11;
            const phaseEnd = phase === 1 ? 5 : phase === 2 ? 10 : 15;
            const phaseCount = 5;
            const answeredInPhase = Object.keys(answers).filter(qid => {
              const qNum = parseInt(qid.replace('Q', ''));
              return qNum >= phaseStart && qNum <= phaseEnd;
            }).length;
            return (
              <div key={phase} className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] font-bold uppercase tracking-wider"
                    style={{ color: currentPhase === phase ? PHASE_META[phase].color : 'var(--t-text-muted)' }}>
                    {PHASE_META[phase].label}
                  </span>
                  <span className="text-[9px]" style={{ color: 'var(--t-text-muted)' }}>Q{PHASE_META[phase].questions}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--t-border)' }}>
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{
                      background: PHASE_META[phase].color,
                      width: `${(answeredInPhase / phaseCount) * 100}%`,
                    }} />
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-right" style={{ color: 'var(--t-text-muted)' }}>
          {answeredCount} of {totalQ} answered ({progressPct}%)
        </p>
      </div>

      {/* Resume Banner */}
      <AnimatePresence>
        {resumed && answeredCount > 0 && currentIdx > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="rounded-xl p-4 mb-6 flex items-center gap-3"
            style={{ background: 'rgba(13,148,136,0.08)', border: '1px solid rgba(13,148,136,0.15)' }}>
            <Play className="w-5 h-5 flex-shrink-0" style={{ color: '#0D9488' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#0D9488' }}>Welcome back!</p>
              <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                You completed {answeredCount} of {totalQ} questions. Resuming where you left off.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Question Card */}
      {currentQ && (
        <AnimatePresence mode="wait">
          <motion.div key={currentQ.id}
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}>
            <div className="nx-card p-6 sm:p-8" data-testid={`question-card-${currentQ.id}`}>
              {/* Question Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${phaseMeta.color}15` }}>
                  <QIcon className="w-5 h-5" style={{ color: phaseMeta.color }} />
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-wider uppercase" style={{ color: phaseMeta.color }}>
                    Question {currentIdx + 1} of {totalQ}
                  </p>
                  <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>{currentQ.title}</h2>
                </div>
              </div>

              {/* Question Text */}
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--t-text-secondary)' }}>
                {currentQ.question}
              </p>

              {/* Input */}
              <div className="mb-6">
                <QuestionInput question={currentQ} value={answers[currentQ.id]}
                  onChange={val => setAnswers(prev => ({ ...prev, [currentQ.id]: val }))} />
                {/* AI Hint Box */}
                <HintBox hint={hints[currentQ.id]} />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button onClick={() => currentIdx > 0 && setCurrentIdx(currentIdx - 1)}
                  disabled={currentIdx === 0}
                  className="flex items-center gap-1.5 text-sm font-medium disabled:opacity-30 transition-colors"
                  style={{ color: 'var(--t-text-muted)' }} data-testid="prev-question-btn">
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>

                <Button onClick={handleSubmit}
                  disabled={!answers[currentQ.id] || submitting}
                  data-testid="submit-answer-btn"
                  className="h-11 px-6 font-semibold rounded-lg"
                  style={{ background: '#0D9488', color: '#fff', opacity: !answers[currentQ.id] ? 0.5 : 1 }}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {currentIdx === totalQ - 1 ? 'Complete Discovery' : 'Continue'}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Insight Card */}
      <AnimatePresence>
        {showInsight && insight && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="mt-5 nx-card p-5" style={{ borderColor: 'rgba(13,148,136,0.2)' }}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(13,148,136,0.1)' }}>
                <Sparkles className="w-4 h-4" style={{ color: '#0D9488' }} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#0D9488' }}>
                  Career Insight
                </p>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
                  {insight}
                </p>
              </div>
            </div>
            {gradeUpdates.length > 0 && (
              <div className="mt-3 pt-3 flex items-center gap-3" style={{ borderTop: '1px solid var(--t-border)' }}>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>
                  Grade Update:
                </span>
                {gradeUpdates.map(g => (
                  <span key={g.dimension} className="px-2 py-0.5 rounded text-xs font-bold"
                    style={{ background: `${g.color}15`, color: g.color }}>
                    {g.name}: ~{g.grade}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Progress Dots */}
      <div className="mt-6 flex items-center justify-center gap-1">
        {questions.map((q, i) => (
          <button key={q.id} onClick={() => answers[q.id] !== undefined && setCurrentIdx(i)}
            className="w-2 h-2 rounded-full transition-all"
            style={{
              background: i === currentIdx ? '#0D9488'
                : answers[q.id] !== undefined ? 'rgba(13,148,136,0.4)'
                : 'var(--t-border)',
              width: i === currentIdx ? 16 : 8,
            }} />
        ))}
      </div>
    </div>
  );
}
